import { IOType } from '@erp/shared-types/node-graph';
import { Handle, Position, type NodeProps, useReactFlow } from '@xyflow/react';
import { IOSpan } from '../node-graph/io-span';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import {
  ArrowDownIcon,
  CaretDownIcon,
  BroadcastIcon,
  GlobeStandIcon,
  PlayIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { useState, useMemo, useCallback } from 'react';
import { Separator } from '@/components/ui/separator';
import { useConnectionDrag } from 'src/pages/authenticated/workflows/context/ConnectionDragContext';
import { useRenderMode } from 'src/pages/authenticated/workflows/context/RenderModeContext';
import {
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  FLOW_CONTROL_SOCKET_TYPE,
  isArraySocketType,
} from '@erp/workflows';
import {
  isFlowConnectionValid,
  isValidConnection,
  isDownstream,
} from 'src/lib/workflows/graph-utils';

interface MMGraphSocket {
  id: string;
  ioType: IOType;
  socketType?: string; // e.g. 'string', 'entity_reference', 'json', 'any', etc.
}

const stepIconStyles = cn('w-3 h-3');
const badgeIconStyles = cn('w-2 h-2');

interface NodeDataType {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  // Optional extras for UI treatments
  stepType?: string; // e.g. 'event_trigger'
  emits?: string[]; // list of event names this step emits
  // Validation state (injected by WorkflowEditor)
  isFloating?: boolean; // Node has no connections at all
  unconnectedRequiredSockets?: Set<string>; // Socket IDs that are required but not connected
  // Rendering mode
  renderMode?: 'default' | 'simple'; // 'simple' = minimal read-only node
}

export function StepNode({ data, id, selected }: NodeProps) {
  const nodeData = data as unknown as NodeDataType;
  const [emitsOpen, setEmitsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { deleteElements, getNodes, getEdges } = useReactFlow();
  const { isDragging, dragInfo, isCompatibleHandle } = useConnectionDrag();
  const { renderMode: contextRenderMode } = useRenderMode();

  // Use node data override if set, otherwise use context
  const renderMode = nodeData.renderMode ?? contextRenderMode;

  const allSockets = useMemo(
    () => [
      ...Object.entries(nodeData.inputs || {}),
      ...Object.entries(nodeData.outputs || {}),
    ],
    [nodeData.inputs, nodeData.outputs],
  );

  const isTrigger = useMemo(() => {
    const t = nodeData.stepType?.toLowerCase() ?? '';
    return t.endsWith('trigger');
  }, [nodeData.stepType]);

  const isEventTrigger = useMemo(() => {
    const t = nodeData.stepType?.toLowerCase() ?? '';
    return isTrigger && t.includes('event');
  }, [isTrigger, nodeData.stepType]);

  const emits = nodeData.emits ?? [];
  const hasEmits = emits.length > 0;

  // Check if we're in an interactive flow (has multiple nodes that can be moved)
  const isInteractive = useMemo(() => {
    const nodes = getNodes();
    return nodes.length > 0;
  }, [getNodes]);

  // Check if this node is compatible with the current drag
  // Include flow sockets in the check for non-trigger nodes
  const isNodeCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    const edges = getEdges();

    // Check if any of our typed handles are compatible
    const hasCompatibleTyped = allSockets.some(([, socket]) => {
      const handleType = socket.ioType === IOType.INPUT ? 'target' : 'source';
      const socketType = socket.socketType || 'any';

      // First check basic type compatibility
      if (!isCompatibleHandle(id, socket.id, handleType, socketType)) {
        return false;
      }

      // For output sockets: check graph position when dragging from flow input
      // (flow inputs accept any type, so we need to validate position)
      if (socket.ioType === IOType.OUTPUT && dragInfo.handleType === 'target') {
        // If this node is downstream of drag origin, output sockets are invalid
        if (isDownstream(dragInfo.nodeId, id, edges)) {
          return false;
        }
      }

      return true;
    });

    // Also check flow sockets for non-trigger nodes
    if (!isTrigger) {
      // Check flow input compatibility
      const flowInTypeCompatible = isCompatibleHandle(
        id,
        FLOW_IN_SOCKET_ID,
        'target',
        FLOW_CONTROL_SOCKET_TYPE,
      );
      const flowInCompatible =
        flowInTypeCompatible &&
        isFlowConnectionValid({
          thisNodeId: id,
          thisHandleType: 'target',
          dragOriginNodeId: dragInfo.nodeId,
          dragOriginHandleType: dragInfo.handleType,
          edges,
        });

      // Check flow output compatibility
      const flowOutTypeCompatible = isCompatibleHandle(
        id,
        FLOW_OUT_SOCKET_ID,
        'source',
        FLOW_CONTROL_SOCKET_TYPE,
      );
      const flowOutCompatible =
        flowOutTypeCompatible &&
        isFlowConnectionValid({
          thisNodeId: id,
          thisHandleType: 'source',
          dragOriginNodeId: dragInfo.nodeId,
          dragOriginHandleType: dragInfo.handleType,
          edges,
        });

      if (flowInCompatible || flowOutCompatible) {
        return true;
      }
    }

    return hasCompatibleTyped;
  }, [
    isDragging,
    isCompatibleHandle,
    id,
    allSockets,
    isTrigger,
    dragInfo,
    getEdges,
  ]);

  // Check if there are any flow edges connected to this node
  const { hasFlowIn, hasFlowOut } = useMemo(() => {
    const edges = getEdges();
    const flowInEdges = edges.filter(
      (e) => e.target === id && e.targetHandle === FLOW_IN_SOCKET_ID,
    );
    const flowOutEdges = edges.filter(
      (e) => e.source === id && e.sourceHandle === FLOW_OUT_SOCKET_ID,
    );
    return {
      hasFlowIn: flowInEdges.length > 0,
      hasFlowOut: flowOutEdges.length > 0,
    };
  }, [getEdges, id]);

  // Check if flow handles are compatible during drag
  const isFlowInCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    // Basic type compatibility check
    const typeCompatible = isCompatibleHandle(
      id,
      FLOW_IN_SOCKET_ID,
      'target',
      FLOW_CONTROL_SOCKET_TYPE,
    );
    if (!typeCompatible) return false;

    // Check graph position to prevent cycles
    return isFlowConnectionValid({
      thisNodeId: id,
      thisHandleType: 'target',
      dragOriginNodeId: dragInfo.nodeId,
      dragOriginHandleType: dragInfo.handleType,
      edges: getEdges(),
    });
  }, [isDragging, isCompatibleHandle, id, dragInfo, getEdges]);

  const isFlowOutCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    // Basic type compatibility check
    const typeCompatible = isCompatibleHandle(
      id,
      FLOW_OUT_SOCKET_ID,
      'source',
      FLOW_CONTROL_SOCKET_TYPE,
    );
    if (!typeCompatible) return false;

    // Check graph position to prevent cycles
    return isFlowConnectionValid({
      thisNodeId: id,
      thisHandleType: 'source',
      dragOriginNodeId: dragInfo.nodeId,
      dragOriginHandleType: dragInfo.handleType,
      edges: getEdges(),
    });
  }, [isDragging, isCompatibleHandle, id, dragInfo, getEdges]);

  // Determine when to show flow sockets:
  // - Always show if connected
  // - Show on hover
  // - Show when dragging a flow control edge (for compatible handles)
  const showFlowIn =
    hasFlowIn || isHovered || (isDragging && isFlowInCompatible);
  const showFlowOut =
    hasFlowOut || isHovered || (isDragging && isFlowOutCompatible);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      deleteElements({ nodes: [{ id }] });
    },
    [deleteElements, id],
  );

  // Get validation state from node data
  const isFloating = nodeData.isFloating ?? false;
  const unconnectedRequiredSockets = nodeData.unconnectedRequiredSockets;

  // Simple mode: minimal read-only node with single left/right handles
  if (renderMode === 'simple') {
    return (
      <div
        className={cn(
          'font-mono rounded-md min-w-[100px] bg-white dark:bg-gray-800 border shadow-sm relative',
          'animate-in fade-in-0 zoom-in-95 duration-300',
          'border-gray-300 dark:border-gray-600',
        )}
      >
        {/* Trigger badge if applicable */}
        {isTrigger && (
          <div className="absolute -top-3 -left-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full shadow p-1 z-10">
            {isEventTrigger ? (
              <BroadcastIcon
                className={cn(
                  stepIconStyles,
                  'text-blue-600 dark:text-blue-400',
                )}
              />
            ) : (
              <PlayIcon
                className={cn(
                  stepIconStyles,
                  'text-blue-600 dark:text-blue-400',
                )}
              />
            )}
          </div>
        )}

        {/* Input handle - only for non-triggers */}
        {!isTrigger && (
          <Handle
            type="target"
            position={Position.Left}
            id="input"
            className="!min-w-2 !min-h-2"
          />
        )}

        {/* Centered label */}
        <div className="px-4 py-2 text-sm text-center font-medium">
          {nodeData.label}
        </div>

        {/* Output handle */}
        <Handle
          type="source"
          position={Position.Right}
          id="output"
          className="!min-w-2 !min-h-2"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'font-mono rounded-md min-w-[150px] bg-white dark:bg-gray-800 border shadow-sm transition-all relative group',
        'animate-in fade-in-0 zoom-in-95 duration-300',
        selected
          ? 'border-blue-500 dark:border-blue-400 shadow-lg ring-2 ring-blue-500/20'
          : isFloating
            ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500/20'
            : 'border-gray-300 dark:border-gray-600 hover:shadow-md',
        isDragging && !isNodeCompatible && 'opacity-40 pointer-events-none',
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={
        isFloating ? 'This node is not connected to the workflow' : undefined
      }
    >
      {/* Delete button - appears on hover in edit mode */}
      {isInteractive && (isHovered || selected) && (
        <button
          type="button"
          onClick={handleDelete}
          className={cn(
            'absolute -top-2 -right-2 z-10',
            'w-6 h-6 rounded-full',
            'bg-red-500 hover:bg-red-600 text-white',
            'flex items-center justify-center',
            'shadow-md hover:shadow-lg',
            'transition-all duration-150',
            'opacity-0 group-hover:opacity-100',
            selected && 'opacity-100',
          )}
          title="Delete node"
        >
          <TrashIcon className="w-3 h-3" />
        </button>
      )}

      {/* Trigger icon badge in top-left if applicable */}
      {isTrigger && (
        <div className="absolute -top-3 -left-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full shadow p-1 z-10">
          {isEventTrigger ? (
            <BroadcastIcon
              className={cn(stepIconStyles, 'text-blue-600 dark:text-blue-400')}
            />
          ) : (
            <PlayIcon
              className={cn(stepIconStyles, 'text-blue-600 dark:text-blue-400')}
            />
          )}
        </div>
      )}

      {/* Flow control handles - ALWAYS rendered so React Flow can track them, but visually hidden when not needed */}
      {!isTrigger && (
        <>
          <Handle
            className={cn(
              '!min-w-2 !min-h-2',
              '!bg-gray-300 dark:!bg-gray-500',
              '!border !border-solid !border-gray-400 dark:!border-gray-400',
              '!flex !items-center !justify-center',
              // Hide when not shown (but keep in DOM for React Flow)
              !showFlowIn && '!opacity-0',
              // Dim when shown but not compatible during drag, show not-allowed cursor
              showFlowIn &&
                !isFlowInCompatible &&
                '!opacity-30 !cursor-not-allowed',
            )}
            type="target"
            position={Position.Top}
            id={FLOW_IN_SOCKET_ID}
            title="Wait For"
            isValidConnection={(connection) =>
              // Prevent connections that would create cycles
              isValidConnection(connection.source ?? '', id, getEdges())
            }
          >
            <ArrowDownIcon
              className={cn(
                'w-2.5 h-2.5 text-gray-600 dark:text-gray-300 pointer-events-none',
                !showFlowIn && 'opacity-0',
              )}
            />
          </Handle>
        </>
      )}
      {!isTrigger && (
        <>
          <Handle
            className={cn(
              '!min-w-2 !min-h-2',
              '!bg-gray-300 dark:!bg-gray-500',
              '!border !border-solid !border-gray-400 dark:!border-gray-400',
              '!flex !items-center !justify-center',
              // Hide when not shown (but keep in DOM for React Flow)
              !showFlowOut && '!opacity-0',
              // Dim when shown but not compatible during drag, show not-allowed cursor
              showFlowOut &&
                !isFlowOutCompatible &&
                '!opacity-30 !cursor-not-allowed',
            )}
            type="source"
            position={Position.Bottom}
            id={FLOW_OUT_SOCKET_ID}
            title="After Complete"
            isValidConnection={(connection) =>
              // Prevent connections that would create cycles
              isValidConnection(id, connection.target ?? '', getEdges())
            }
          >
            <ArrowDownIcon
              className={cn(
                'w-2.5 h-2.5 text-gray-600 dark:text-gray-300 pointer-events-none',
                !showFlowOut && 'opacity-0',
              )}
            />
          </Handle>
        </>
      )}

      <div>
        <h5
          className={cn(
            'text-sm rounded-t-sm px-2 py-1 font-bold mb-4 text-center whitespace-nowrap text-ellipsis overflow-hidden',
            selected
              ? 'bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100'
              : 'bg-blue-100 dark:bg-blue-900 text-blue-900 dark:text-blue-100',
          )}
          title={nodeData.label}
        >
          {nodeData.label}
        </h5>
      </div>

      <div className="p-2">
        {allSockets.map(([socketName, socket]) => {
          const handleType =
            socket.ioType === IOType.INPUT ? 'target' : 'source';
          const socketType = socket.socketType || 'any';

          // Basic type compatibility check
          let isHandleCompatible =
            !isDragging ||
            isCompatibleHandle(id, socket.id, handleType, socketType);

          // For output sockets: if dragging from a flow input (which accepts any type),
          // also check graph position to prevent cycles
          if (
            isHandleCompatible &&
            isDragging &&
            socket.ioType === IOType.OUTPUT &&
            dragInfo?.handleType === 'target' &&
            dragInfo.nodeId
          ) {
            // If this node is downstream of the drag origin, output sockets are invalid
            if (isDownstream(dragInfo.nodeId, id, getEdges())) {
              isHandleCompatible = false;
            }
          }

          // Use composite key to handle steps that have same socket.id for both input and output
          // (e.g., inboundEmail pass-through in extract-document-content step)
          const socketKey = `${handleType}-${socket.id}`;

          // Check if this input socket is required but not connected
          const isRequiredUnconnected =
            socket.ioType === IOType.INPUT &&
            unconnectedRequiredSockets?.has(socket.id);

          // Check if this is an array socket (supports multiple inputs via fan-in)
          const isArraySocket = isArraySocketType(socketType);

          return (
            <div
              key={socketKey}
              className={cn(
                'relative flex items-center w-full',
                socket.ioType === IOType.OUTPUT && 'justify-end',
              )}
            >
              <IOSpan text={socketName} ioType={socket.ioType} />
              {/* Red asterisk for required unconnected input sockets */}
              {isRequiredUnconnected && (
                <span
                  className="text-red-500 font-bold ml-0.5 text-xs"
                  title="Required input - must be connected"
                >
                  *
                </span>
              )}
              <Handle
                className={cn(
                  '!min-w-2 !min-h-2 !top-1/2 !transform-none !-translate-y-1/2',
                  socket.ioType === IOType.INPUT ? '!-left-3' : '!-right-3',
                  // Grey out incompatible handles during drag, show not-allowed cursor
                  !isHandleCompatible && '!opacity-30 !cursor-not-allowed',
                  // Array sockets get a distinct style to indicate they accept multiple inputs
                  isArraySocket &&
                    socket.ioType === IOType.INPUT &&
                    '!border-2 !border-dashed !border-purple-500 dark:!border-purple-400',
                )}
                type={socket.ioType === IOType.INPUT ? 'target' : 'source'}
                position={
                  socket.ioType === IOType.INPUT
                    ? Position.Left
                    : Position.Right
                }
                id={socket.id}
                title={
                  isArraySocket && socket.ioType === IOType.INPUT
                    ? `${socketName} (accepts multiple connections)`
                    : socketName
                }
              />
            </div>
          );
        })}
      </div>

      {/* Emits drawer: slim badge row by default; expands on click */}
      {hasEmits && (
        <div className="overflow-hidden rounded-b-sm">
          <Separator className="px-0" />
          <div className="p-1 bg-accent">
            <button
              type="button"
              className="w-full cursor-pointer"
              onClick={() => setEmitsOpen((v) => !v)}
            >
              <div className="flex gap-2 items-center">
                <CaretDownIcon
                  className={cn(
                    stepIconStyles,
                    'transition-transform duration-200',
                    emitsOpen ? '-rotate-90' : '',
                  )}
                />
                <Badge variant="outline" className="gap-1">
                  <GlobeStandIcon className={badgeIconStyles} />
                  {emits.length}
                </Badge>
              </div>
            </button>

            <div
              className={cn(
                'overflow-hidden transition-all',
                emitsOpen ? 'max-h-64 mt-2' : 'max-h-0',
              )}
            >
              <div className="border-t border-dashed border-gray-300 dark:border-gray-600 pt-2 space-y-3">
                {/* Section header with badge and rule */}
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="gap-1">
                    <GlobeStandIcon className={badgeIconStyles} />
                    Emits
                  </Badge>
                  <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                </div>
                <div className="space-y-1">
                  {emits.map((evt) => (
                    <div
                      key={evt}
                      className="text-[11px] px-2 py-1 rounded bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700"
                    >
                      {evt}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
